import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  FlipCameraAndroid as FlipIcon,
  TouchApp as TouchIcon,
  ZoomOutMap as ZoomOutIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const FlippingCards = ({ content }) => {
  const theme = useTheme();

  // State для отслеживания состояния переворота карточек (теперь массив)
  const [flippedCards, setFlippedCards] = useState({});
  // State для расширенного просмотра карточки
  const [expandedCard, setExpandedCard] = useState(null);

  // Parse the content JSON
  let cards = [];
  try {
    // Проверяем, что content определен и не пустой
    if (content) {
      cards = JSON.parse(content);
      if (!Array.isArray(cards)) {
        cards = [];
      }
    } else {
      console.warn('FlippingCards: content is undefined or empty');
    }
  } catch (error) {
    console.error('Error parsing card content:', error);
    return (
      <Box>
        <Typography color="error">
          Ошибка загрузки контента. Пожалуйста, свяжитесь с поддержкой.
        </Typography>
      </Box>
    );
  }

  // Переворачивание конкретной карточки
  const handleFlip = (index) => {
    setFlippedCards((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Переключение режима расширенного просмотра
  const handleExpand = (index) => {
    if (expandedCard === index) {
      setExpandedCard(null); // Закрыть расширенный просмотр
    } else {
      setExpandedCard(index); // Открыть расширенный просмотр
    }
  };

  if (cards.length === 0) {
    return (
      <Box>
        <Typography>Нет доступных карточек.</Typography>
      </Box>
    );
  }

  // Компонент для отображения одной карточки
  const CardItem = ({ card, index, isExpanded }) => {
    const isFlipped = flippedCards[index] || false;
    const cardHeight = isExpanded ? 500 : 280;

    return (
      <Box
        sx={{ height: cardHeight, mb: 4, position: 'relative', width: '100%' }}
      >
        {/* Кнопка расширения */}
        <Tooltip title={isExpanded ? 'Свернуть' : 'Развернуть'}>
          <IconButton
            size="small"
            sx={{
              position: 'absolute',
              right: 10,
              top: 10,
              zIndex: 100,
              backgroundColor: 'rgba(255,255,255,0.7)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleExpand(index);
            }}
          >
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Контейнер для карточки с точно такой же структурой как в MemoryGame */}
        <Box
          sx={{
            height: '100%',
            perspective: '1000px',
            cursor: 'pointer',
          }}
          onClick={() => handleFlip(index)}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              transformStyle: 'preserve-3d',
              transition: 'transform 0.6s',
              transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0)',
            }}
          >
            {/* Передняя сторона (видимая до переворота) */}
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                backfaceVisibility: 'hidden',
                backgroundColor: theme.palette.background.paper,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                overflow: 'auto',
                padding: 2,
                '&:hover': {
                  boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
                },
              }}
            >
              <Box sx={{ width: '100%', overflow: 'auto', maxHeight: '100%' }}>
                <Typography variant="h5" gutterBottom>
                  {card.front.title}
                </Typography>
                <Box
                  sx={{
                    mt: 2,
                    '& table': {
                      borderCollapse: 'collapse',
                      width: '100%',
                      mb: 2,
                    },
                    '& th, & td': {
                      border: '1px solid',
                      borderColor: 'divider',
                      p: 1,
                      textAlign: 'left',
                    },
                    '& th': {
                      backgroundColor: 'background.default',
                      fontWeight: 'bold',
                    },
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {card.front.content}
                  </ReactMarkdown>
                </Box>
                {!isFlipped && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mt: 2,
                      opacity: 0.7,
                      '&:hover': { opacity: 1 },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <TouchIcon fontSize="small" sx={{ mr: 0.5 }} /> Нажмите,
                      чтобы перевернуть
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Задняя сторона (видимая после переворота) */}
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
                backgroundColor: theme.palette.background.default,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 2,
                borderRadius: '8px',
                overflow: 'auto',
              }}
            >
              <Box sx={{ width: '100%', overflow: 'auto', maxHeight: '100%' }}>
                <Typography variant="h5" gutterBottom>
                  {card.back.title}
                </Typography>
                <Box
                  sx={{
                    mt: 2,
                    '& table': {
                      borderCollapse: 'collapse',
                      width: '100%',
                      mb: 2,
                    },
                    '& th, & td': {
                      border: '1px solid',
                      borderColor: 'divider',
                      p: 1,
                      textAlign: 'left',
                    },
                    '& th': {
                      backgroundColor: 'background.default',
                      fontWeight: 'bold',
                    },
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {card.back.content}
                  </ReactMarkdown>
                </Box>
                {isFlipped && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mt: 2,
                      opacity: 0.7,
                      '&:hover': { opacity: 1 },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <TouchIcon fontSize="small" sx={{ mr: 0.5 }} /> Нажмите,
                      чтобы перевернуть
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', my: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 2,
          textAlign: 'center',
          mb: 3,
          backgroundColor: theme.palette.primary.light,
          color: theme.palette.primary.contrastText,
        }}
      >
        <Typography variant="h6">
          Карточки для обучения - нажмите на карточку, чтобы перевернуть
        </Typography>
      </Paper>

      {/* Отображение карточек в сетке или расширенно */}
      {expandedCard !== null ? (
        // Отображение одной карточки в расширенном режиме
        <CardItem
          card={cards[expandedCard]}
          index={expandedCard}
          isExpanded={true}
        />
      ) : (
        // Отображение всех карточек в сетке
        <Grid container spacing={3}>
          {cards.map((card, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={index}
              sx={{ height: '100%' }}
            >
              <CardItem card={card} index={index} isExpanded={false} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Инструкции для пользователя */}
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          <FlipIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
          Нажмите на карточку, чтобы увидеть ответ.
          <ZoomOutIcon
            fontSize="small"
            sx={{ verticalAlign: 'middle', ml: 2, mr: 1 }}
          />
          Используйте кнопку в углу для увеличения карточки.
        </Typography>
      </Box>
    </Box>
  );
};

export default FlippingCards;
